import { setCompanyThemePolicy } from './useTheme';
import type { Company, ThemeKey } from '@/types';

const STYLE_ID = 'pk-company-css';
/**
 * The parts the sign-in screen still needs after a sign-out: there is no
 * company context before authentication, so the last one used on this device
 * stands in. Only public file ids and labels are cached — never colours or CSS.
 */
const CACHE_KEY = 'pro-kanban.branding';

interface CachedBranding {
  faviconFileId?: string | null;
  loginBackgroundFileId?: string | null;
  hideProductName?: boolean;
  companyName?: string | null;
}

/** The favicon and title that shipped in index.html, restored when unset. */
let defaultFavicon: string | null = null;
const defaultTitle = __APP_NAME__;

/**
 * URL for a branding image.
 *
 * Branding uploads are stored public, so they can be referenced straight from a
 * `<link>` or `background-image` — neither of which can send an auth header.
 */
export function publicFileUrl(fileId: string): string {
  return `${__API_BASE_URL__}/files/public/${encodeURIComponent(fileId)}`;
}

function faviconLink(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.append(link);
  }
  if (defaultFavicon === null) defaultFavicon = link.getAttribute('href') ?? '';
  return link;
}

function applyFavicon(fileId?: string | null): void {
  const link = faviconLink();
  const next = fileId ? publicFileUrl(fileId) : defaultFavicon ?? '';
  if (link.getAttribute('href') !== next) link.setAttribute('href', next);
}

/** Company CSS lives in one managed tag, appended last so it wins on ties. */
function applyCustomCss(css?: string | null): void {
  const existing = document.querySelector<HTMLStyleElement>(`#${STYLE_ID}`);
  if (!css?.trim()) {
    existing?.remove();
    return;
  }
  const style = existing ?? document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = css;
  if (!existing) document.head.append(style);
}

/** With the product name hidden, the tab carries the company instead. */
function applyTitle(name?: string | null, hideProductName?: boolean): void {
  document.title = hideProductName && name ? name : defaultTitle;
}

function cache(entry: CachedBranding | null): void {
  try {
    if (!entry) localStorage.removeItem(CACHE_KEY);
    else localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // A full or blocked storage only costs us the pre-auth branding.
  }
}

export function cachedBranding(): CachedBranding {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as CachedBranding) : {};
  } catch {
    return {};
  }
}

/**
 * Applies everything a company brands: theme rules, colours, CSS, favicon, title.
 *
 * Called whenever the active company changes or its settings are saved.
 */
export function applyCompanyBranding(company: Company | null): void {
  const branding = company?.branding ?? null;

  setCompanyThemePolicy(
    branding
      ? {
          controlsTheme: branding.controlsTheme === true,
          allowedThemes: (branding.allowedThemes ?? []) as ThemeKey[],
          overrideColors: branding.overrideColors === true,
          accentColor: branding.accentColor,
          secondaryColor: branding.secondaryColor,
        }
      : null,
  );
  applyCustomCss(branding?.customCss);
  applyFavicon(branding?.faviconFileId);
  applyTitle(company?.name, branding?.hideProductName);

  cache(
    branding
      ? {
          faviconFileId: branding.faviconFileId ?? null,
          loginBackgroundFileId: branding.loginBackgroundFileId ?? null,
          hideProductName: branding.hideProductName === true,
          companyName: company?.name ?? null,
        }
      : null,
  );
}

/**
 * Drops the company chrome on sign-out.
 *
 * The favicon deliberately stays: it is cached from the last session and is
 * what the sign-in screen should show.
 */
export function releaseCompanyBranding(): void {
  setCompanyThemePolicy(null);
  applyCustomCss(null);
}

/**
 * Restores the favicon and tab title before any company is loaded.
 *
 * Runs on boot from the cache written by the last session, so the tab is right
 * on the sign-in screen instead of flipping once the workspace loads.
 */
export function initCompanyBranding(): void {
  const cached = cachedBranding();
  applyFavicon(cached.faviconFileId);
  applyTitle(cached.companyName, cached.hideProductName);
}
